'use client';

import { useConvex, useConvexAuth } from 'convex/react';
import { useLocale, useTranslations } from 'next-intl';
import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';

import { normalizeLocale, type SupportedLocale } from '@/i18n/config';
import { persistAppLocaleChange } from '@/lib/app-locale-change';
import { api, useMutation } from '@/lib/convex';

/**
 * Changes the signed-in user's app language from anywhere (account menu,
 * command menu). Persists exactly what Profile settings persists, then does
 * the same full reload so server-rendered copy, `lang` and `dir` all switch.
 */
export function useChangeAppLocale(): {
  currentLocale: SupportedLocale;
  pendingLocale: SupportedLocale | null;
  changeLocale: (locale: string) => Promise<void>;
} {
  const t = useTranslations('WorkspaceSwitcher');
  const currentLocale = normalizeLocale(useLocale());
  const convex = useConvex();
  const { isAuthenticated } = useConvexAuth();
  const updateRegionalSettings = useMutation(
    api.users.mutations.updateRegionalSettings
  );
  const inFlightRef = useRef(false);
  const [pendingLocale, setPendingLocale] = useState<SupportedLocale | null>(
    null
  );

  const changeLocale = useCallback(
    async (locale: string) => {
      const nextLocale = normalizeLocale(locale);
      if (inFlightRef.current || nextLocale === currentLocale) return;

      inFlightRef.current = true;
      setPendingLocale(nextLocale);
      try {
        await persistAppLocaleChange({
          locale: nextLocale,
          isAuthenticated,
          fetchProfile: () => convex.query(api.users.queries.getProfile, {}),
          updateRegionalSettings,
        });
      } catch (error) {
        inFlightRef.current = false;
        setPendingLocale(null);
        console.error('[change-app-locale] failed to persist locale', error);
        toast.error(t('languageChangeFailed'));
        return;
      }
      window.location.reload();
    },
    [convex, currentLocale, isAuthenticated, t, updateRegionalSettings]
  );

  return { currentLocale, pendingLocale, changeLocale };
}
